import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Box from '@mui/material/Box';

// Leaflet map for the Video Canvass Tracker. Renders one pin per video-pull
// location (colored by status, with an optional direction-of-travel arrow),
// supports tap-to-drop and drag-to-move, and reuses the OSM tile + teardown
// idioms already used by CameraMapModal / LPRMapReplay.

const DEFAULT_CENTER = { lat: 41.03, lng: -73.76, zoom: 13 };

// Colored dot + optional rotated arrow + optional "needs follow-up" dashed
// ring, drawn as a divIcon (no image assets, so no bundler marker-icon issues).
function pinIcon(color, arrowDeg, flagged) {
  const arrow = arrowDeg == null ? '' :
    `<div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%) rotate(${arrowDeg}deg);">
       <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
         <line x1="12" y1="21" x2="12" y2="6"/><polyline points="6 12 12 6 18 12"/>
       </svg>
     </div>`;
  const ring = flagged
    ? `<div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:26px;height:26px;border-radius:50%;border:2px dashed #ea580c;"></div>`
    : '';
  const dot = `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 1.5px ${color};"></div>`;
  return L.divIcon({
    html: `<div style="position:relative;width:34px;height:34px;display:flex;align-items:center;justify-content:center;">${arrow}${ring}${dot}</div>`,
    className: 'canvass-pin',
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

const pendingIcon = L.divIcon({
  html: `<div style="width:22px;height:22px;border-radius:50%;background:rgba(109,40,217,.3);border:3px solid #6d28d9;box-shadow:0 0 0 4px rgba(109,40,217,.2);"></div>`,
  className: 'canvass-pin-pending',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

export default function CanvassMap({
  points = [],
  center,
  pending = null,
  addMode = false,
  focusId = null,
  onMapClick,
  onMarkerClick,
  onMarkerDragEnd,
  onPendingDragEnd,
  height = 460,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const pendingMarkerRef = useRef(null);
  const didFitRef = useRef(false);
  // Latest props for the map's persistent event handlers.
  const propsRef = useRef({});
  propsRef.current = { addMode, onMapClick, onMarkerClick, onMarkerDragEnd, onPendingDragEnd };

  // Init map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const c = center || DEFAULT_CENTER;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    map.setView([c.lat, c.lng], c.zoom || DEFAULT_CENTER.zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    markerLayerRef.current = L.layerGroup().addTo(map);
    map.on('click', e => {
      if (propsRef.current.addMode && propsRef.current.onMapClick) {
        propsRef.current.onMapClick(e.latlng.lat, e.latlng.lng);
      }
    });
    mapRef.current = map;
    const t = setTimeout(() => { if (mapRef.current === map) map.invalidateSize(false); }, 60);
    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
      markerLayerRef.current = null;
      pendingMarkerRef.current = null;
      // Reset the one-shot fit so a fresh map instance (e.g. StrictMode's
      // dev re-mount) re-fits to the existing pins instead of staying at
      // the default center.
      didFitRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Crosshair cursor while in add mode.
  useEffect(() => {
    if (containerRef.current) containerRef.current.style.cursor = addMode ? 'crosshair' : '';
  }, [addMode]);

  // Re-center when the caller changes the center (e.g. "use my location", a
  // geocoded address pick).
  useEffect(() => {
    if (mapRef.current && center) mapRef.current.setView([center.lat, center.lng], center.zoom || mapRef.current.getZoom());
  }, [center]);

  // Rebuild saved-point markers on any change.
  useEffect(() => {
    const layer = markerLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const p of points) {
      if (p.lat == null || p.lng == null) continue;
      const m = L.marker([p.lat, p.lng], { icon: pinIcon(p.color || '#6d28d9', p.arrowDeg, p.flagged), draggable: true });
      if (p.label) m.bindTooltip(p.label, { direction: 'top' });
      m.on('click', () => propsRef.current.onMarkerClick && propsRef.current.onMarkerClick(p.id));
      m.on('dragend', ev => {
        // Already-placed pins are easy to bump by accident, so confirm before
        // persisting the move; the new-pin/pending marker (see below) isn't
        // gated since the user is still actively positioning it.
        if (!window.confirm('Move this pin to the new location?')) {
          ev.target.setLatLng([p.lat, p.lng]);
          return;
        }
        const ll = ev.target.getLatLng();
        propsRef.current.onMarkerDragEnd && propsRef.current.onMarkerDragEnd(p.id, ll.lat, ll.lng);
      });
      m.addTo(layer);
    }
    // Fit to all points once on first load if the caller gave no explicit center.
    // maxZoom keeps a single pin (or tight cluster) from slamming to street level.
    if (!didFitRef.current && !center && points.length && mapRef.current) {
      const b = L.latLngBounds(points.map(p => [p.lat, p.lng]));
      if (b.isValid()) mapRef.current.fitBounds(b.pad(0.25), { maxZoom: 16 });
      didFitRef.current = true;
    }
  }, [points, center]);

  // The not-yet-saved dropped/edited pin.
  useEffect(() => {
    if (!mapRef.current) return;
    if (pendingMarkerRef.current) {
      pendingMarkerRef.current.remove();
      pendingMarkerRef.current = null;
    }
    if (pending && pending.lat != null && pending.lng != null) {
      const m = L.marker([pending.lat, pending.lng], { icon: pendingIcon, draggable: true, zIndexOffset: 1000 });
      m.on('dragend', ev => {
        const ll = ev.target.getLatLng();
        propsRef.current.onPendingDragEnd && propsRef.current.onPendingDragEnd(ll.lat, ll.lng);
      });
      m.addTo(mapRef.current);
      pendingMarkerRef.current = m;
    }
  }, [pending]);

  // Pan to a focused point (row/marker selected in the list).
  useEffect(() => {
    if (!focusId || !mapRef.current) return;
    const p = points.find(x => x.id === focusId);
    if (p) mapRef.current.panTo([p.lat, p.lng], { animate: true });
  }, [focusId]); // eslint-disable-line react-hooks/exhaustive-deps

  return <Box ref={containerRef} sx={{ height, width: '100%', borderRadius: '10px', overflow: 'hidden', zIndex: 0 }} />;
}
